import { supabase } from '../supabaseClient';
import type {
  PlatformWorkspaceAccountRow,
  SchoolUserAdminStats,
  TeamDetailRow,
  TeamMemberSummaryRow,
  TeamMembershipRowCore,
  UserStatDetailKind,
  UserStatDetailPayload,
} from '../types/adminUserStats';
import {
  loadAdminUserStatDetailCache,
  loadAdminUserStatsCache,
  saveAdminUserStatDetailCache,
  saveAdminUserStatsCache,
} from '../utils/platformGlobalAdminCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  parseSubscriptionPassStateRecord,
  resolveAccountPassCategory,
  type AccountPassCategory,
} from '../utils/subscriptionPassStorage';

export type {
  PlatformWorkspaceAccountRow,
  SchoolUserAdminStats,
  TeamDetailRow,
  TeamMemberSummaryRow,
  TeamMembershipRowCore,
  UserStatDetailKind,
  UserStatDetailPayload,
} from '../types/adminUserStats';

export type { AccountPassCategory } from '../utils/subscriptionPassStorage';
export { ACCOUNT_PASS_CATEGORY_LABEL, ACCOUNT_PASS_CATEGORY_PARTS } from '../utils/subscriptionPassStorage';

function emptyPassCategoryCounts(): NonNullable<SchoolUserAdminStats['passCategoryCounts']> {
  return { beta: 0, free: 0, eventFree: 0, eventPaid: 0, individual: 0, group: 0 };
}

async function loadPassCategoryByUserId(): Promise<Map<string, AccountPassCategory>> {
  const map = new Map<string, AccountPassCategory>();
  try {
    const { data, error } = await supabase
      .from('platform_user_subscription_passes')
      .select(
        'user_id, individual_history, group_history, event_history, event_pass_period, session_group_pass_binding',
      );
    if (error) {
      if (!isMissingTableError(error.message)) console.error(error);
      return map;
    }
    for (const raw of data ?? []) {
      const row = raw as Record<string, unknown>;
      const userId = String(row.user_id ?? '');
      if (!userId) continue;
      const state = parseSubscriptionPassStateRecord({
        individualHistory: row.individual_history,
        groupHistory: row.group_history,
        eventHistory: row.event_history,
        eventPassPeriod: row.event_pass_period,
        sessionGroupPassBinding: row.session_group_pass_binding,
      });
      map.set(userId, resolveAccountPassCategory(state));
    }
  } catch (e) {
    console.error(e);
  }
  return map;
}

async function loadPassCategoryCounts(
  registeredAccountCount: number | null,
): Promise<SchoolUserAdminStats['passCategoryCounts']> {
  if (registeredAccountCount == null || registeredAccountCount < 0) return null;
  const counts = emptyPassCategoryCounts();
  try {
    const byUser = await loadPassCategoryByUserId();
    let nonFree = 0;
    byUser.forEach((cat) => {
      if (cat === 'free') return;
      counts[cat] += 1;
      nonFree += 1;
    });
    counts.free = Math.max(0, registeredAccountCount - nonFree);
    return counts;
  } catch (e) {
    console.error(e);
    return null;
  }
}

function isMissingTableError(message: string): boolean {
  return (
    message.includes('Could not find the table') ||
    message.includes('schema cache') ||
    message.includes('does not exist')
  );
}

function isMissingRpcError(message: string): boolean {
  return message.includes('Could not find the function') || message.includes('schema cache');
}

async function fetchRegisteredAccountCount(): Promise<number | null> {
  try {
    const { data, error } = await supabase.rpc('admin_platform_workspace_account_count');
    if (error) {
      if (isMissingRpcError(error.message)) return null;
      throw new Error(error.message);
    }
    const n = data as unknown;
    const count = typeof n === 'number' ? n : n != null ? Number(n) : 0;
    return Number.isNaN(count) ? null : count;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (isMissingRpcError(msg)) return null;
    throw e;
  }
}

async function fetchTeamCount(workspaceId: string): Promise<number | null> {
  const { count, error } = await supabase
    .from('teams')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId);
  if (error) {
    if (isMissingTableError(error.message)) return null;
    throw new Error(error.message);
  }
  return count ?? 0;
}

async function fetchPassLedgerLatestUpdatedAt(): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('platform_user_subscription_passes')
      .select('updated_at')
      .order('updated_at', { ascending: false })
      .limit(1);
    if (error) {
      if (isMissingTableError(error.message)) return '';
      return '';
    }
    const row = (data ?? [])[0] as Record<string, unknown> | undefined;
    return row?.updated_at != null ? String(row.updated_at) : '';
  } catch {
    return '';
  }
}

/** 계정수·팀수·이용권 원장 최신 갱신시각만 가볍게 조합한 head 시그니처. 값이 같으면 무거운 이용권 집계(loadPassCategoryCounts)를 생략할 수 있다. */
async function fetchSchoolUserAdminStatsHeadVersion(
  workspaceId: string
): Promise<{ version: string; registeredAccountCount: number | null; teamCount: number | null }> {
  const [registeredAccountCount, teamCount, passLatest] = await Promise.all([
    fetchRegisteredAccountCount(),
    fetchTeamCount(workspaceId),
    fetchPassLedgerLatestUpdatedAt(),
  ]);
  const version = `${registeredAccountCount ?? 'x'}|${teamCount ?? 'x'}|${passLatest}`;
  return { version, registeredAccountCount, teamCount };
}

/**
 * 관리자 화면용: 첫 카드는 플랫폼 전체 등록 계정 수, 나머지는 해당 워크스페이스(`workspace_id`) 기준 팀 지표.
 * LS 캐시가 있으면 head(계정수·팀수·이용권 최신 updated_at)만 먼저 비교하고, 동일하면 캐시를 재사용한다.
 */
export async function fetchSchoolUserAdminStats(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<SchoolUserAdminStats> {
  const sid = workspaceId.trim();
  const empty: SchoolUserAdminStats = {
    registeredAccountCount: null,
    teamCount: 0,
    passCategoryCounts: null,
  };
  if (!sid) return empty;

  const cached = options?.forceRefresh ? null : loadAdminUserStatsCache(sid);
  let head: { version: string; registeredAccountCount: number | null; teamCount: number | null } | null = null;
  if (cached?.found && cached.version) {
    head = await fetchSchoolUserAdminStatsHeadVersion(sid);
    if (head.version === cached.version) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('이용자통계·집계·LS(head일치)');
      }
      return cached.stats;
    }
  } else if (cached?.found) {
    // 과거에 버전 없이 저장된 캐시: 즉시 표시는 페이지 쪽에서 처리했을 것이므로 여기선 항상 최신 재조회.
  }

  const registeredAccountCount = head ? head.registeredAccountCount : await fetchRegisteredAccountCount();

  let teamCount: number;
  try {
    teamCount = head?.teamCount ?? (await fetchTeamCount(sid)) ?? 0;
  } catch (e) {
    if (e instanceof Error && isMissingTableError(e.message)) {
      const passCategoryCounts = await loadPassCategoryCounts(registeredAccountCount);
      const partial: SchoolUserAdminStats = { ...empty, registeredAccountCount, passCategoryCounts };
      saveAdminUserStatsCache(sid, partial, { force: true });
      return partial;
    }
    throw e;
  }

  const passCategoryCounts = await loadPassCategoryCounts(registeredAccountCount);

  const stats: SchoolUserAdminStats = {
    registeredAccountCount,
    teamCount,
    passCategoryCounts,
  };
  const passLatest = head ? null : await fetchPassLedgerLatestUpdatedAt();
  const version = head?.version ?? `${registeredAccountCount ?? 'x'}|${teamCount}|${passLatest}`;
  saveAdminUserStatsCache(sid, stats, { force: true, version });
  return stats;
}

function memberRoleKo(role: string): string {
  const m: Record<string, string> = {
    owner: '소유자',
    admin: '관리자',
    teacher: '교사',
    viewer: '뷰어',
  };
  return m[role] ?? role;
}

/** 팀 생성자는 DB상 `team_members.role`이 teacher인 경우가 있어, 생성자면 팀장으로 표시 */
export function displayTeamMembershipRole(row: {
  user_id: string;
  role: string;
  team_created_by: string | null;
}): string {
  const leader = (row.team_created_by ?? '').trim();
  if (leader && leader === row.user_id) {
    return '팀장';
  }
  return memberRoleKo(row.role);
}

async function loadUserDirectoryMap(workspaceId: string): Promise<Map<string, { email: string; display_name: string }>> {
  const { data, error } = await supabase.rpc('admin_user_directory_for_school', { p_school_id: workspaceId });
  if (error) throw new Error(error.message);
  const map = new Map<string, { email: string; display_name: string }>();
  for (const raw of data ?? []) {
    const row = raw as Record<string, unknown>;
    map.set(String(row.user_id), {
      email: String(row.email ?? ''),
      display_name: String(row.display_name ?? ''),
    });
  }
  return map;
}

function pickProfile(
  map: Map<string, { email: string; display_name: string }>,
  userId: string
): { email: string; display_name: string } {
  const p = map.get(userId);
  const email = (p?.email ?? '').trim();
  const display_name = (p?.display_name ?? '').trim();
  return {
    email: email.length > 0 ? email : '—',
    display_name: display_name.length > 0 ? display_name : '—',
  };
}

async function loadTeamsMap(workspaceId: string): Promise<{ ids: string[]; byId: Map<string, TeamDetailRow> }> {
  const { data, error } = await supabase
    .from('teams')
    .select('id, name, code, member_limit, created_at, created_by')
    .eq('workspace_id', workspaceId)
    .order('name', { ascending: true });
  if (error) {
    if (isMissingTableError(error.message)) return { ids: [], byId: new Map() };
    throw new Error(error.message);
  }
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    id: String(r.id),
    name: String(r.name ?? ''),
    code: String(r.code ?? ''),
    member_limit: r.member_limit != null ? Number(r.member_limit) : null,
    created_at: r.created_at != null ? String(r.created_at) : null,
    created_by: r.created_by != null ? String(r.created_by) : null,
  })) as TeamDetailRow[];
  const byId = new Map(rows.map((r) => [r.id, r]));
  return { ids: rows.map((r) => r.id), byId };
}

async function loadMembershipRows(teamIds: string[], byId: Map<string, TeamDetailRow>): Promise<TeamMembershipRowCore[]> {
  if (teamIds.length === 0) return [];
  const { data, error } = await supabase
    .from('team_members')
    .select('team_id, user_id, role, status, is_active, joined_at')
    .in('team_id', teamIds)
    .order('joined_at', { ascending: false });
  if (error) {
    if (isMissingTableError(error.message)) return [];
    throw new Error(error.message);
  }
  const raw = data ?? [];
  return raw.map((r: Record<string, unknown>) => {
    const tid = String(r.team_id);
    const team = byId.get(tid);
    return {
      team_id: tid,
      team_name: team?.name ?? '(팀)',
      team_created_by: team?.created_by != null ? String(team.created_by) : null,
      user_id: String(r.user_id),
      role: String(r.role ?? ''),
      status: String(r.status ?? ''),
      is_active: Boolean(r.is_active),
      joined_at: r.joined_at != null ? String(r.joined_at) : null,
    };
  });
}

async function loadAllPlatformAccounts(): Promise<PlatformWorkspaceAccountRow[]> {
  const { data, error } = await supabase.rpc('admin_platform_workspace_accounts');
  if (error) {
    if (isMissingRpcError(error.message)) return [];
    throw new Error(error.message);
  }
  const categoryByUser = await loadPassCategoryByUserId();
  const raw = data ?? [];
  return raw.map((r: Record<string, unknown>) => {
    const user_id = String(r.user_id);
    return {
      user_id,
      email: String(r.email ?? ''),
      display_name: String(r.display_name ?? ''),
      workspace_name: String(r.workspace_name ?? ''),
      workspace_created_at: r.workspace_created_at != null ? String(r.workspace_created_at) : null,
      last_sign_in_at: r.last_sign_in_at != null ? String(r.last_sign_in_at) : null,
      last_active_on: r.last_active_on != null ? String(r.last_active_on).slice(0, 10) : null,
      team_name: r.team_name != null && String(r.team_name).trim() !== '' ? String(r.team_name).trim() : null,
      team_role: r.team_role != null ? String(r.team_role) : null,
      team_created_by: r.team_created_by != null ? String(r.team_created_by) : null,
      pass_category: categoryByUser.get(user_id) ?? 'free',
    };
  });
}

/** 통계 카드별 상세 목록. `forceRefresh` 없으면 LS 우선. */
export async function fetchUserStatDetail(
  workspaceId: string,
  kind: UserStatDetailKind,
  options?: { forceRefresh?: boolean }
): Promise<UserStatDetailPayload> {
  const sid = workspaceId.trim();
  if (!sid) {
    switch (kind) {
      case 'allAccounts':
        return { kind: 'allAccounts', rows: [] };
      case 'teamsWithMembers':
        return { kind: 'teamsWithMembers', teams: [], membersByTeamId: {} };
    }
  }

  if (!options?.forceRefresh) {
    const cached = loadAdminUserStatDetailCache(sid, kind);
    if (cached.found && cached.payload) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('이용자통계·상세·LS');
      }
      return cached.payload;
    }
  }

  if (kind === 'allAccounts') {
    const rows = await loadAllPlatformAccounts();
    const payload: UserStatDetailPayload = { kind: 'allAccounts', rows };
    saveAdminUserStatDetailCache(sid, kind, payload, { force: true });
    return payload;
  }

  if (kind === 'teamsWithMembers') {
    const { ids, byId } = await loadTeamsMap(sid);
    const teams = Array.from(byId.values());
    if (ids.length === 0) {
      const emptyPayload: UserStatDetailPayload = { kind: 'teamsWithMembers', teams: [], membersByTeamId: {} };
      saveAdminUserStatDetailCache(sid, kind, emptyPayload, { force: true });
      return emptyPayload;
    }
    const allRows = await loadMembershipRows(ids, byId);
    const approved = allRows.filter((r) => r.status === 'approved');
    const map = await loadUserDirectoryMap(sid);
    const membersByTeamId: Record<string, TeamMemberSummaryRow[]> = {};
    for (const id of ids) {
      membersByTeamId[id] = [];
    }
    for (const r of approved) {
      const role_display = displayTeamMembershipRole(r);
      const list = membersByTeamId[r.team_id];
      if (list) {
        list.push({
          user_id: r.user_id,
          role_display,
          ...pickProfile(map, r.user_id),
        });
      }
    }
    for (const id of ids) {
      const arr = membersByTeamId[id] ?? [];
      arr.sort((a, b) => {
        const wa = a.role_display === '팀장' ? 0 : 1;
        const wb = b.role_display === '팀장' ? 0 : 1;
        if (wa !== wb) return wa - wb;
        return a.email.localeCompare(b.email);
      });
    }
    const payload: UserStatDetailPayload = { kind: 'teamsWithMembers', teams, membersByTeamId };
    saveAdminUserStatDetailCache(sid, kind, payload, { force: true });
    return payload;
  }

  throw new Error('지원하지 않는 상세 종류입니다.');
}
